
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, UserCheck, BarChart3, Shield, FileText } from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { CourseCountBadge } from '@/components/common/CourseCountBadge';
import StaffAnalyticsDashboard from '@/components/staff/StaffAnalyticsDashboard';
import StaffCertificateManagement from '@/components/staff/StaffCertificateManagement';
import StaffCourseOverview from '@/components/staff/StaffCourseOverview';
import { StudentProgressReport } from '@/components/reports/StudentProgressReport';
import { BulkCourseUnassignManager } from '@/components/staff/BulkCourseUnassignManager';

const StaffDashboard: React.FC = () => {
  const { hasAccess, isLoading, userRole, user } = useSupabaseAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  // Debug logging to track auth state
  React.useEffect(() => {
    console.log('🎯 STAFF DASHBOARD - Auth State:', {
      isLoading,
      userRole,
      userId: user?.id,
      userEmail: user?.email,
      hasStaffAccess: hasAccess('staff'),
      timestamp: new Date().toISOString()
    });
  }, [isLoading, userRole, user, hasAccess]);

  // Show loading state while role is being fetched
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess('staff')) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have staff permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Staff Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive analytics, user management, and certificate administration
          </p>
          <div className="mt-3">
            <CourseCountBadge showIcons={true} />
          </div>
        </div>
        <Badge variant="secondary" className="flex items-center space-x-2">
          <Shield className="h-4 w-4" />
          <span>Staff Member</span>
        </Badge>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={(value) => setSearchParams({ tab: value })}
        className="w-full"
      >
        <TabsList className="mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Bulk Operations
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Certificates
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <StaffCourseOverview />
        </TabsContent>

        <TabsContent value="bulk" className="space-y-6">
          <BulkCourseUnassignManager />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <StaffAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="certificates" className="space-y-6">
          <StaffCertificateManagement />
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Student Progress Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StudentProgressReport />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default StaffDashboard;
